import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Constants } from 'app/components/utils/Constants';
import { Log } from 'app/core/logging/log';
import { DiscountModel, DiscountPeriodUnit } from 'app/models/discount-model';
import { PriceModel } from 'app/models/price-model';
import { ProductModel } from 'app/models/product-model';
import { AppService } from 'app/services/app/app.service';
import { RestService } from 'app/services/rest/rest.service';
import { firstValueFrom, map, tap } from 'rxjs';


@Component({
  selector: 'app-admin-discount',
  templateUrl: './admin-discount.page.html',
  styleUrls: ['./admin-discount.page.scss'],
})
export class AdminDiscountPage implements OnInit {

  displayedColumns: string[] = ['code', 'description', 'discount', 'productIds'];
  dataSource = new MatTableDataSource<DiscountModel>;
  products: Map<number, ProductModel>;
  discounts: DiscountModel[] = [];

  constructor(
    private dialog: MatDialog,
    private restService: RestService,
    public appService: AppService
  ) { }

  async ngOnInit() {
    this.products = await this.getProducts();
    this.getDiscounts();
  }

  discountRowClass = (row: DiscountModel) => ({
    disabled: !row?.enabled
  });

  openNewDiscount() {
    this.openDetails();
  }

  openDetails(discount?: DiscountModel) {
    let dialogRef = this.dialog.open(DiscountModalContent, {
      panelClass: 'responsive-dialog',
      data: { discount: discount, products: Array.from(this.products.values()) }
    });
    dialogRef.afterClosed().subscribe((data: { updated: boolean }) => data?.updated && this.getDiscounts());
  }

  getDiscounts() {
    this.restService.getDiscounts().subscribe(result => {
      this.discounts = result;
      this.dataSource = new MatTableDataSource(result);
    });
  }

  async getProducts(): Promise<Map<number, ProductModel>> {
    return await firstValueFrom(this.restService.getProducts().pipe(
      map(list =>
        new Map<number, ProductModel>(list.map(p => [p.id as number, p]))
      ))
    );
  }

  descriptionProduct(productIds: number[]): string {
    let result:string[] = [];
    if (productIds) {
      productIds.forEach((productId: number) => {
        const id = Number(productId);
        if(this.products != null && this.products.has(id)){
          const desc = this.products.get(id)?.category?.code || '';
          result.push(desc);
        }
        
      });
    }
    return result.toString();
  }

}


@Component({
  selector: 'app-discount-modal-content',
  templateUrl: './discount-modal.component.html',
  styleUrls: ['./discount-modal.component.scss']
})
export class DiscountModalContent implements OnInit {
  isNew: boolean;
  products: ProductModel[] = [];
  form!: FormGroup;
  readonly periodUnitOptions: DiscountPeriodUnit[] = ['MINUTES', 'HOURS', 'DAYS'];

  constructor(
    private fb: FormBuilder,
    private restService: RestService,
    public appService: AppService,
    private dialogRef: MatDialogRef<DiscountModalContent>,
    @Inject(MAT_DIALOG_DATA) public data: { discount: DiscountModel, products: ProductModel[] }
  ) { }


  ngOnInit(): void {
    const discount:DiscountModel = this.data?.discount ?? {} as DiscountModel;
    this.isNew = this.data?.discount == null;
    this.products = this.data?.products ?? [];
    Log.debug('pr', {product: this.products});
    this.initForm(discount);
  }

  initForm(discount: DiscountModel): void {
    this.form = this.fb.group({
      id: [discount.id ?? null],
      code: [discount.code ?? null, Validators.required],
      description: [discount.description ?? '', Validators.required],
      discountType: [discount.discountType ?? 'PERCENTAGE'],
      discount: [discount.discount ?? 0, Validators.required],
      productIds: [discount.productIds ?? []],
      priceIds: [discount.priceIds ?? []],
      relatedData: [discount.related?.data ?? ''],
      relatedDataType: [discount.related?.dataType ?? ''],
      relatedType: [discount.related?.type ?? null],
      earlyPeriod: [discount.earlyPeriod ?? null],
      offsetPeriod: [discount.offsetPeriod ?? null],
      periodUnit: [discount.periodUnit ?? null],
      offsetProduct: [discount.offsetProduct ?? null],
      maxUse: [discount.maxUse ?? null],
      timesUsed: [discount.timesUsed ?? null],
      expirationDate: [discount.expirationDate ? new Date(discount.expirationDate) : null],
      enabled: [discount.enabled ?? true, Validators.required]
    });

    this.form.get('productIds')?.valueChanges.subscribe(() => this.syncSelectedPriceIds());
  }

  get selectedProducts(): ProductModel[] {
    const selectedProductIds = new Set<number>((this.form?.get('productIds')?.value ?? []).map((id: number) => Number(id)));
    if (!selectedProductIds.size) {
      return this.products;
    }

    return this.products.filter(product => selectedProductIds.has(Number(product.id)));
  }

  trackByProductId(index: number, product: ProductModel): number {
    return Number(product.id);
  }

  trackByPriceId(index: number, price: PriceModel): number {
    return Number(price.id);
  }

  productLabel(product: ProductModel): string {
    return product?.category?.code || `#${product?.id ?? ''}`;
  }

  priceLabel(price: PriceModel): string {
    if (!price) {
      return '';
    }

    const range = price.minPeriod != null && price.maxPeriod != null
      ? ` (${price.minPeriod}-${price.maxPeriod} ${price.unit})`
      : '';

    return `${price.price} EUR / ${price.period} ${price.unit}${range}`;
  }

  private syncSelectedPriceIds(): void {
    const priceIdsControl = this.form.get('priceIds');
    const currentPriceIds = (priceIdsControl?.value ?? []).map((id: number) => Number(id));
    const allowedPriceIds = new Set<number>(
      this.selectedProducts.flatMap(product => (product.prices ?? []).map(price => Number(price.id)))
    );
    const nextPriceIds = currentPriceIds.filter((id: number) => allowedPriceIds.has(id));

    if (nextPriceIds.length !== currentPriceIds.length) {
      priceIdsControl?.setValue(nextPriceIds, { emitEvent: false });
    }
  }

  confirm(): void {
    if (this.form.invalid) return;
    const discount = this.prepareDiscount();
    Log.debug('Guardar descuento', discount);

    if (this.isNew) {
      this.restService.postDiscount(discount).subscribe({
        next: res => {
          this.dialogRef.close({ updated: true }); 
          this.appService.showSuccessToast(Constants.MSG_SUCCESS);
        }
      });
    } else {
      this.restService.putDiscount(discount).subscribe({
        next: res => {
          this.dialogRef.close({ updated: true }); 
          this.appService.showSuccessToast(Constants.MSG_SUCCESS);
        }
      });
    }
  }

  deleteDiscount(): void {
    if (!confirm('¿Está seguro de eliminar este descuento?')) return;
    const id = this.form.value.id;
    this.restService.deleteDiscount(id).subscribe({
      next: res => {
          this.dialogRef.close({ updated: true }); 
          this.appService.showSuccessToast(Constants.MSG_SUCCESS);
        }
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  private prepareDiscount() {
    const value = this.form.value;
    const raw = this.form.getRawValue();
    const productIds = (value.productIds ?? []).map((id: number) => Number(id));
    const allowedPriceIds = new Set<number>(
      this.products
        .filter(product => productIds.includes(Number(product.id)))
        .flatMap(product => (product.prices ?? []).map(price => Number(price.id)))
    );
    const priceIds = (value.priceIds ?? [])
      .map((id: number) => Number(id))
      .filter((id: number) => !productIds.length || allowedPriceIds.has(id));

    return {
      ...value,
      productIds,
      priceIds,
      related: {
        data: value.relatedData,
        dataType: value.relatedDataType,
        type: value.relatedType,
      },
      expirationDate: raw.expirationDate
      ? (raw.expirationDate instanceof Date
          ? raw.expirationDate.getTime()
          : new Date(raw.expirationDate).getTime())
      : null
    };
  }

}
